import { axiosConfig } from "@/services/axiosConfig";
import {
  buildPageParams,
  extractList,
  extractPaginationMeta,
  type PaginatedResult,
} from "@/projects/lib/pagination";

const PATHSHALAS_PATH = "/rajnagar_gyanmahostav/pathshalas/";

/** Raw API record — fields may vary slightly; mapper is defensive. */
export type ApiPathshala = {
  id?: string | number;
  pk?: string | number;
  name?: string;
  title?: string;
  is_active?: boolean;
  active?: boolean;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
};

export type Pathshala = {
  id: string;
  name: string;
  isActive: boolean;
};

export type PathshalaWritePayload = {
  name: string;
  is_active?: boolean;
};

export function mapApiPathshalaToPathshala(row: ApiPathshala): Pathshala {
  const id = String(row.id ?? row.pk ?? row.name ?? "");
  const active =
    typeof row.is_active === "boolean"
      ? row.is_active
      : typeof row.active === "boolean"
        ? row.active
        : true;
  return {
    id,
    name: row.name?.trim() || row.title?.trim() || "—",
    isActive: active,
  };
}

function detailPath(id: string) {
  return `${PATHSHALAS_PATH}${encodeURIComponent(id)}/`;
}

function unwrapOne(
  payload: ApiPathshala | { data?: ApiPathshala }
): ApiPathshala {
  if (payload && typeof payload === "object" && "data" in payload && payload.data) {
    return payload.data as ApiPathshala;
  }
  return payload as ApiPathshala;
}

export async function fetchPathshalasPage(params?: {
  page?: number;
  pageSize?: number;
  isActive?: boolean;
  search?: string;
}): Promise<PaginatedResult<Pathshala>> {
  const query: Record<string, string> = {
    ...buildPageParams(params?.page, params?.pageSize),
  };
  if (params?.isActive !== undefined) {
    query.is_active = String(params.isActive);
  }
  if (params?.search) {
    query.search = params.search;
  }
  const { data } = await axiosConfig.get(PATHSHALAS_PATH, {
    params: query,
    headers: { Accept: "application/json" },
  });
  const list = extractList<ApiPathshala>(data);
  const items = list.map(item =>
    mapApiPathshalaToPathshala((item ?? {}) as ApiPathshala)
  );
  return {
    items,
    pagination: extractPaginationMeta(data, items.length, params?.page ?? 1),
  };
}

export async function fetchPathshalas(params?: {
  isActive?: boolean;
  search?: string;
}): Promise<Pathshala[]> {
  const { items } = await fetchPathshalasPage({ ...params, pageSize: 1000 });
  return items;
}

export async function createPathshala(
  payload: PathshalaWritePayload
): Promise<Pathshala> {
  const { data } = await axiosConfig.post<ApiPathshala | { data: ApiPathshala }>(
    PATHSHALAS_PATH,
    payload,
    { headers: { "Content-Type": "application/json", Accept: "application/json" } }
  );
  return mapApiPathshalaToPathshala(unwrapOne(data));
}

export async function updatePathshala(
  id: string,
  payload: Partial<PathshalaWritePayload>
): Promise<Pathshala> {
  const { data } = await axiosConfig.patch<ApiPathshala | { data: ApiPathshala }>(
    detailPath(id),
    payload,
    { headers: { "Content-Type": "application/json", Accept: "application/json" } }
  );
  return mapApiPathshalaToPathshala(unwrapOne(data));
}

export async function setPathshalaActive(
  id: string,
  isActive: boolean
): Promise<Pathshala> {
  return updatePathshala(id, { is_active: isActive });
}

export async function deletePathshala(id: string): Promise<void> {
  await axiosConfig.delete(detailPath(id), {
    headers: { Accept: "application/json" },
  });
}

export function getPathshalaApiErrorMessage(error: unknown, fallback: string) {
  const err = error as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const data = err?.response?.data;
  if (!data) {
    return err?.message || fallback;
  }
  for (const key of ["message", "detail", "error", "non_field_errors"]) {
    const v = data[key];
    if (typeof v === "string" && v.trim()) {
      return v;
    }
    if (Array.isArray(v) && typeof v[0] === "string") {
      return v[0];
    }
  }
  for (const [k, v] of Object.entries(data)) {
    if (Array.isArray(v) && typeof v[0] === "string") {
      return `${k}: ${v[0]}`;
    }
    if (typeof v === "string" && v.trim() && k !== "status" && k !== "success") {
      return v;
    }
  }
  return fallback;
}
